/*    
<summary>
  This functional component "AddShiftManagement" defines the props like initial values and functions for the child form component.
  Developer:Sahil Bharti, Created Date:06-Jan-2025
</summary>
<returns>Returns JSX</returns>
*/

import { observer } from "mobx-react";
import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { cloneDeep } from "lodash";
import { useStore } from "../../../contexts/store-provider";

import { formatMessage } from "../../../translations/format-message";
import { businessTypeOptions } from "../../../constants/options.constant";
import AddShiftManagementForm from "../forms/add-edit-shift-form";
import { IAddShiftManagement } from "../../../models/forms/IAddShiftManagement";
import DeletePopup from "../../../shared-components/popup/delete/delete-popup";

interface IProps {
  id: number;
  modalClosed: () => void;
  storeId: number;
  dates: Date[];
  shiftStatus: number;
}

const AddShiftManagement = (props: IProps) => {
  const { shiftManagementStore, masterManagementStore } = useStore();
  const {
    GetShiftManagementByIdService,
    resetAddUpdateShiftManagementState,
    resetShiftManagementDetail,
    shiftState,
    shiftDetail,
    addUpdateShiftManagementState,
    AddShiftManagementService,
    UpdateShiftManagementService,
    GetShiftManagementListByStoreIdService,
    GetAllShiftScheduleListByStoreIdService,
    inProgress,
    DeleteShiftManagementService,
    deleteShiftManagementState,
    resetDeleteShiftManagementState,
    GetShiftManagementListFilterByStoreIdService
  } = shiftManagementStore;
  const {reservationSettingBusinessVM} = masterManagementStore;
  const initialValues: IAddShiftManagement = shiftDetail;
  const { dates } = props;
  const [showDeletePopup, setShowDeletePopup] = useState(false);

  /**
   * The useEffect Hook lets you perform side effects in function component.
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered
   * while a dependency of the useEffect has changed.
   */
  useEffect(() => {
    if (props.id > 0) {
      GetShiftManagementByIdService(props.id);
    }
  }, []);

  /**
   * This function closes the pop up modal and reset getstaffdetails, addUpdate staff observables (success, inprogress
   * and error).
   */
  const onModalClosed = useCallback(() => {
    resetShiftManagementDetail();
    resetAddUpdateShiftManagementState();
    // resetDeleteShiftManagementState();
    props.modalClosed();
  }, [props, resetAddUpdateShiftManagementState, resetShiftManagementDetail]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "addUpdateStaffManagementSuccess" of the useEffect changed.
   */
  useEffect(() => {
    if (addUpdateShiftManagementState.success) {
      if (props.id === -1) toast.success(formatMessage("added_success"));
      else toast.success(formatMessage("updated_success"));
      GetShiftManagementListFilterByStoreIdService(props.storeId, props.shiftStatus, reservationSettingBusinessVM);
      GetAllShiftScheduleListByStoreIdService(
        props.storeId,
        dates[0],
        dates[dates.length - 1]
      );
      onModalClosed();
    }
  }, [
    GetShiftManagementListFilterByStoreIdService,
    GetAllShiftScheduleListByStoreIdService,
    addUpdateShiftManagementState.success,
    dates,
    onModalClosed,
    props.id,
    props.storeId,
  ]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "staffDetailError" of the useEffect changed.
   */
  useEffect(() => {
    if (shiftState.error) {
      toast.error(formatMessage(shiftState.error));
    }
  }, [shiftState.error]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "addUpdateStaffManagementError" of the useEffect changed.
   */
  useEffect(() => {
    if (addUpdateShiftManagementState.error) {
      toast.error(formatMessage(addUpdateShiftManagementState.error));
      resetAddUpdateShiftManagementState();
    }
  }, [addUpdateShiftManagementState.error]);

  /**
   * This function adds the staff when "Id < 0" and updates the Existing staff when Id > 0 by providing form values to the
   * correponding function
   */
  const onSubmitForm = (values: IAddShiftManagement) => {
    let tempValues = cloneDeep(values);
    tempValues.ShiftName = values.ShiftName.trim();
    if (props.id < 0)
      AddShiftManagementService({ ...tempValues, StoreId: props.storeId });
    else
      UpdateShiftManagementService(props.id, {
        ...tempValues,
        StoreId: props.storeId,
      });
  };

  const handleDeleteConfirm = () => {
    DeleteShiftManagementService(props.id);
  };

  useEffect(() => {
    if (deleteShiftManagementState.success) {
      setShowDeletePopup(false);
      onModalClosed();
      GetAllShiftScheduleListByStoreIdService(
        props.storeId,
        dates[0],
        dates[dates.length - 1]
      );
    }
  }, [
    setShowDeletePopup,
    deleteShiftManagementState.success,
    onModalClosed,
    GetAllShiftScheduleListByStoreIdService,
    props.storeId,
    dates,
  ]);

  return (
    <React.Fragment>
      {showDeletePopup && (
        <DeletePopup
          modalSubmit={handleDeleteConfirm}
          modalClosed={() => setShowDeletePopup(false)}
          isLoading={deleteShiftManagementState.inProgress}
          title={formatMessage("delete_confirmation")}
          message={formatMessage("are_you_sure_you_want_to_delete_this_shift?")}
        />
      )}
      <AddShiftManagementForm
        initialValues={initialValues}
        id={props.id}
        submitHandler={onSubmitForm}
        onModalClosed={onModalClosed}
        businessTypeOptions={businessTypeOptions}
        getShiftManagementLoading={shiftState.inProgress || inProgress}
        addUpdateShiftManagementLoading={
          addUpdateShiftManagementState.inProgress
        }
        showDeletePopup={showDeletePopup}
        setShowDeletePopup={setShowDeletePopup}
      />
    </React.Fragment>
  );
};

export default observer(AddShiftManagement);
